import { StorageService } from '../../services/storage/storage.service';
import { v4 as uuid } from 'uuid';
import { AppStateService } from '../../services/appState/app-state.service';
import { WaitingService } from '../../features/layout/waiting/waiting.service';
import { BehaviorSubject, from } from 'rxjs';
import { WancomHttpService } from '../../services/wancom-http/wancom-http.service';
import { HasId } from './edit/HasId';

export class RepositoryService<TItem extends HasId> extends BehaviorSubject<FilterResult<TItem>> {
	protected readonly OFFLINE_STORAGE_KEY: string = null;

	constructor(
		protected http: WancomHttpService,
		protected waiting$: WaitingService,
		protected url: string,
		protected appStateService: AppStateService = null,
		protected storageService: StorageService = null,
		protected offlineStorageKey: string = null
	) {
		super({ items: [], total: 0 });
		if (offlineStorageKey) {
			this.OFFLINE_STORAGE_KEY = offlineStorageKey;
		}
	}

	getAll(filterParams?: FilterParams, path?: string, otherBaseUrl?: string) {
		const call = from(this.getAllAsync(filterParams, path, otherBaseUrl));
		call.subscribe((result) => {
			this.next(result);
		});
		return call;
	}

	async getAllAsync(filterParams?: FilterParams, path?: string, otherBaseUrl?: string) {
		if (this.OFFLINE_STORAGE_KEY && (await this.appStateService.isOfflineMode())) {
			return this.getAllOffline();
		} else {
			return this.getAllOnline(filterParams, path, otherBaseUrl).toPromise();
		}
	}

	getAllOnline(filterParams: FilterParams = {}, path: string = null, otherBaseUrl?: string) {
		let url = this.url;
		if (path) url += '/' + path;

		return this.http.post<FilterResult<TItem>>(url, filterParams, otherBaseUrl);
	}

	async getAllOffline() {
		const items = await this.storageService.getItem<FilterResult<TItem>>(this.OFFLINE_STORAGE_KEY);
		return items;
	}

	get(id: any) {
		return from(this.getAsync(id));
	}

	async getAsync(id: any) {
		if (this.offlineStorageKey && (await this.appStateService.isOfflineMode())) {
			return this.getOffline(id);
		} else {
			return this.getOnline(id).toPromise();
		}
	}

	getOnline(id) {
		return this.http.get<TItem>(`${this.url}/${id}`);
	}

	async getOffline(id) {
		const item = await this.storageService.getItem<TItem>(id);
		if (item) {
			return item;
		}
		const filteredResult = await this.getAllOffline();
		return filteredResult.items.find((o) => o.id == id);
	}

	edit(item: TItem) {
		return from(this.editAsync(item));
	}

	async editAsync(item: TItem, STORAGE_KEY: string = null) {
		if (!this.appStateService || !(await this.appStateService.isOfflineMode())) {
			return this.editOnline(item);
		} else {
			return this.editOffline(item, STORAGE_KEY);
		}
	}

	async editOnline(item: TItem) {
		if (item.id) return this.http.post<TItem>(`${this.url}/${item.id}`, item).toPromise();
		else return this.http.put<TItem>(this.url, item).toPromise();
	}

	async editOffline(item: TItem, STORAGE_KEY: string = null) {
		if (!item.id) {
			STORAGE_KEY = STORAGE_KEY || this.OFFLINE_STORAGE_KEY;
			item.id = uuid();
		}

		await this.storageService.addItemInFilteredResult(STORAGE_KEY, item.id, item);
		this.storageService.setItem('NEW_' + STORAGE_KEY + '_' + item.id, item);

		return item;
	}

	delete(id: any) {
		return from(this.deleteAsync(id));
	}

	async deleteAsync(id: any) {
		if (!this.appStateService || !(await this.appStateService.isOfflineMode())) {
			return this.deleteOnlineAsync(id);
		} else {
			return this.deleteOfflineAsync(id);
		}
	}

	async deleteOnlineAsync(id: any) {
		return this.http.delete<void>(`${this.url}/${id}`).toPromise();
	}

	async deleteOfflineAsync(id: any) {
		const idNumber = +id;
		const isGuid = isNaN(idNumber);

		// Pas besoin de supprimer un item sur le back si il n'a pas été sync
		if (!isGuid) {
			this.storageService.setItem('DELETE_' + this.OFFLINE_STORAGE_KEY + '_' + id, id);
		}

		await this.storageService.deleteItemInFilteredResult(this.OFFLINE_STORAGE_KEY, id);
	}

	clear() {
		this.next({ items: [], total: 0 });
	}

	async downloadOffline(filterParams?: FilterParams) {
		const res = await this.getAllAsync(filterParams);
		this.storageService.setItem(this.OFFLINE_STORAGE_KEY, res);
	}
}

export class FilterParams {
	page?: number;
	pageSize?: number;
	sortColumn?: string;
	sortDirection?: SortDirection;
	objectMode?: string;
	advancedFilter?: boolean;
}

export class FilterResult<TItem> {
	items: TItem[];
	total: number;
}

export enum SortDirection {
	Asc,
	Desc
}
